import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, WhatsappLogo, InstagramLogo, ShareNetwork, Link as LinkIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const bookUrl = () => `${window.location.origin}/books/agamali-kaeleer`;
const caption = (p) => `“${p.title_ta}” — a poem from Agamali Kaeleer by Shunmugavel.`;

function ShareRow({ poem }) {
  const url = bookUrl();
  const text = `${caption(poem)}\n${url}`;

  const nativeShare = async () => {
    try {
      const res = await fetch(poem.image_url);
      const blob = await res.blob();
      const file = new File([blob], `${poem.poem_id}.png`, { type: blob.type });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: caption(poem), title: "Agamali Kaeleer" });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: "Agamali Kaeleer", text: caption(poem), url });
        return;
      }
      throw new Error("unsupported");
    } catch (e) {
      if (e?.name === "AbortError") return;
      await navigator.clipboard?.writeText(text);
      toast.success("Link and caption copied — paste it anywhere");
    }
  };

  const forInstagram = async () => {
    try {
      await navigator.clipboard?.writeText(caption(poem) + "\n" + url);
    } catch {}
    const a = document.createElement("a");
    a.href = poem.image_url;
    a.download = `${poem.poem_id}.png`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Page saved & caption copied — post it on Instagram");
  };

  const copyLink = async () => {
    await navigator.clipboard?.writeText(text);
    toast.success("Link copied");
  };

  const btn = "inline-flex items-center gap-1.5 rounded-sm border border-foreground/15 px-3 py-2 text-xs font-semibold transition-colors duration-200 hover:border-primary hover:text-primary";

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="poem-share-row">
      <a className={btn} href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp-button">
        <WhatsappLogo size={16} weight="fill" /> WhatsApp
      </a>
      <button type="button" className={btn} onClick={forInstagram} data-testid="share-instagram-button">
        <InstagramLogo size={16} weight="fill" /> Instagram
      </button>
      <button type="button" className={btn} onClick={nativeShare} data-testid="share-native-button">
        <ShareNetwork size={16} weight="fill" /> Share
      </button>
      <button type="button" className={btn} onClick={copyLink} data-testid="share-copy-button">
        <LinkIcon size={16} weight="bold" /> Copy link
      </button>
    </div>
  );
}

export function PoemPreview() {
  const [open, setOpen] = useState(null);
  const track = useRef(null);
  const { data: poems = [] } = useQuery({ queryKey: ["poems"], queryFn: async () => (await api.get("/poems")).data });
  const poem = poems.find((p) => p.poem_id === open);

  const scrollBy = (dir) => {
    const el = track.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 900), behavior: "smooth" });
  };

  const arrow = "flex h-10 w-10 items-center justify-center rounded-sm border border-foreground/15 transition-colors duration-200 hover:border-primary hover:text-primary";

  if (!poems.length) return null;

  return (
    <section id="poems" className="py-20 md:py-24" data-testid="poems-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-5 border-b border-foreground/15 pb-6">
          <div>
            <p className="eyebrow">From the pages</p>
            <h2 className="mt-2 font-display text-base font-medium sm:text-lg">{poems.length} poems, before you buy the eighty-six.</h2>
          </div>
          <div className="flex items-center gap-3">
            <p className="hidden text-sm text-muted-foreground sm:block">Tap a page to read & share</p>
            <button type="button" onClick={() => scrollBy(-1)} className={arrow} aria-label="Previous poems" data-testid="poems-prev-button"><ArrowLeft size={16} weight="bold" /></button>
            <button type="button" onClick={() => scrollBy(1)} className={arrow} aria-label="More poems" data-testid="poems-next-button"><ArrowRight size={16} weight="bold" /></button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div
          ref={track}
          className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="poems-track"
        >
        {poems.map((p, i) => (
          <motion.button
            key={p.poem_id}
            type="button"
            onClick={() => setOpen(p.poem_id)}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: Math.min(i, 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
            className="group w-[240px] shrink-0 snap-start text-left sm:w-[280px]"
            data-testid={`poem-card-${p.poem_id}`}
          >
            <div className="overflow-hidden rounded-sm border border-foreground/10 bg-white">
              <img
                src={p.image_url}
                alt={p.title_ta}
                loading="lazy"
                className="aspect-[7/10] w-full bg-[#F5F3ED] object-contain transition-transform duration-500 group-hover:scale-[1.03]"
              />
            </div>
            <p className="mt-4 font-display text-lg leading-snug transition-colors duration-200 group-hover:text-primary">{p.title_ta}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{p.title_en}</p>
          </motion.button>
        ))}
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-[min(92vw,560px)] border-foreground/10 bg-background p-3 sm:p-4" data-testid="poem-dialog">
          <DialogTitle className="font-display text-lg font-medium">{poem?.title_ta}</DialogTitle>
          {poem && (
            <>
              <img src={poem.image_url} alt={poem.title_ta} className="max-h-[68vh] w-full rounded-sm object-contain" data-testid="poem-dialog-image" />
              <ShareRow poem={poem} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
